import type { History } from 'history';
import _ from 'lodash';
import {
  FilterInfo,
  FilterOperationInfo,
  SavedSearchInfo,
  UrlPagination,
} from 'types/pagination';

import { getUserData } from './authService';
import { DEFAULT_PAGINATION } from './tableUtils';

export enum UrlParamKey {
  PAGINATION = 'pagination',
  FILTERS = 'filters',
  FILTER_OPERATIONS = 'filterOperations',
  EXTRAS = 'extras',
  SAVED_SEARCH = 'savedSearch',
  GROUP_BY = 'groupBy',
  IS_VISUAL_MODE = 'isVisualMode',
}

export function parseURLSearch(search: string = '', skipDefault?: boolean) {
  const urlParams = new URLSearchParams(search);
  let pagination = skipDefault ? undefined : { ...DEFAULT_PAGINATION };
  if (urlParams.has(UrlParamKey.PAGINATION)) {
    pagination = _.assign(
      pagination || {},
      JSON.parse(urlParams.get(UrlParamKey.PAGINATION) as string),
    );
  }
  const filters = urlParams.has(UrlParamKey.FILTERS)
    ? JSON.parse(urlParams.get(UrlParamKey.FILTERS) as string)
    : {};
  const filterOperations = urlParams.has(UrlParamKey.FILTER_OPERATIONS)
    ? JSON.parse(urlParams.get(UrlParamKey.FILTER_OPERATIONS) as string)
    : {};
  const extras = urlParams.has(UrlParamKey.EXTRAS)
    ? JSON.parse(urlParams.get(UrlParamKey.EXTRAS) as string)
    : {};
  const savedSearch = urlParams.has(UrlParamKey.SAVED_SEARCH)
    ? JSON.parse(urlParams.get(UrlParamKey.SAVED_SEARCH) as string)
    : {};
  const groupBy = urlParams.has(UrlParamKey.GROUP_BY)
    ? JSON.parse(urlParams.get(UrlParamKey.GROUP_BY) as string)
    : undefined;
  // Parse isVisualMode - undefined for legacy views (use existing parsing logic)
  const isVisualModeParam = urlParams.get(UrlParamKey.IS_VISUAL_MODE);
  const isVisualMode =
    isVisualModeParam === 'true'
      ? true
      : isVisualModeParam === 'false'
        ? false
        : undefined;
  return {
    pagination,
    filters,
    savedSearch,
    groupBy,
    extras,
    filterOperations,
    isVisualMode,
  };
}

export function updateURLSearch(
  history: History,
  {
    pagination,
    filters = {},
    filterOperations = {},
    savedSearch,
    groupBy,
    extras = undefined,
    isVisualMode,
  }: {
    pagination?: UrlPagination;
    filters?: FilterInfo;
    filterOperations?: FilterOperationInfo;
    savedSearch?: SavedSearchInfo;
    groupBy?: string[];
    extras?: FilterInfo;
    isVisualMode?: boolean;
  },
  pathname?: string,
) {
  const userData = getUserData();
  if (userData?.sessionData?.nonRegisteredUser) {
    return;
  }
  if (!savedSearch) {
    savedSearch = parseURLSearch(history?.location?.search).savedSearch;
  }
  const urlParams = getUrlSearch(
    {
      pagination,
      filters,
      filterOperations,
      savedSearch,
      groupBy,
      extras,
      isVisualMode,
    },
    history?.location?.search || '',
  );
  const urlParamsObject = Object.fromEntries(new URLSearchParams(urlParams));
  const historyParamsObject = Object.fromEntries(
    new URLSearchParams(history?.location?.search.substring(1)),
  );
  let pushUrl = true;
  if (typeof pathname === 'undefined') {
    pathname = history.location.pathname;
    pushUrl = false;
  }
  /**
   * If any url doesn't start with the /u/ pattern, it means it has old URL format
   * so we need to append it to prevent the entire UI DOM from getting refresh
   * We aren't checking tenant or BU because BU will change when user switches to another BU and the condition breaks
   */
  const prePath = '/u/';
  if (!pathname.startsWith(prePath) && !pathname.includes('superAdmin')) {
    pathname = `/u/${userData.tenantId}/${userData.businessUnitId}${pathname}`;
  }

  history[pushUrl ? 'push' : 'replace'](`${pathname}?${urlParams}`);
  return !_.isEqual(urlParamsObject, historyParamsObject);
}

export function getUrlSearch(
  {
    pagination,
    filters = {},
    filterOperations = {},
    savedSearch,
    groupBy,
    extras = undefined,
    isVisualMode,
  }: {
    pagination?: UrlPagination;
    filters?: FilterInfo;
    filterOperations?: FilterOperationInfo;
    savedSearch?: SavedSearchInfo;
    groupBy?: string[];
    extras?: FilterInfo;
    isVisualMode?: boolean;
  },
  search = window.location.search,
  removePagination: boolean = false,
) {
  const urlParams = new URLSearchParams(search);
  if (!_.isEmpty(pagination)) {
    urlParams.set(UrlParamKey.PAGINATION, JSON.stringify(pagination));
  }
  // removeing null filters to make the URL clean
  filters = _.pickBy(filters, (v) => v !== null && v !== undefined);
  if (!_.isEmpty(filters)) {
    urlParams.set(UrlParamKey.FILTERS, JSON.stringify(filters));
  } else {
    urlParams.delete(UrlParamKey.FILTERS);
  }
  if (!_.isEmpty(filterOperations)) {
    urlParams.set(
      UrlParamKey.FILTER_OPERATIONS,
      JSON.stringify(filterOperations),
    );
  } else {
    urlParams.delete(UrlParamKey.FILTER_OPERATIONS);
  }
  if (!_.isEmpty(extras)) {
    urlParams.set(UrlParamKey.EXTRAS, JSON.stringify(extras));
  } else {
    urlParams.delete(UrlParamKey.EXTRAS);
  }
  if (!_.isEmpty(savedSearch)) {
    urlParams.set(UrlParamKey.SAVED_SEARCH, JSON.stringify(savedSearch));
  } else {
    urlParams.delete(UrlParamKey.SAVED_SEARCH);
  }
  if (!_.isEmpty(groupBy)) {
    urlParams.set(UrlParamKey.GROUP_BY, JSON.stringify(groupBy));
  } else {
    urlParams.delete(UrlParamKey.GROUP_BY);
  }
  if (removePagination) {
    urlParams.delete(UrlParamKey.PAGINATION);
  }
  // Handle isVisualMode - only set when explicitly provided
  if (isVisualMode !== undefined) {
    urlParams.set(UrlParamKey.IS_VISUAL_MODE, String(isVisualMode));
  } else {
    urlParams.delete(UrlParamKey.IS_VISUAL_MODE);
  }
  return urlParams.toString();
}

/**
 * Parses the current URL search string and returns the params
 * with the "extras" key stripped out — ready to use when building
 * a new navigation URL that should not carry over transient extras state.
 */
export function parseURLSearchWithoutExtras(search: string) {
  const { extras: _extras, ...rest } = parseURLSearch(search, true);
  return rest;
}

export function parseURLHash(hash = '') {
  hash = hash || '';
  const questionMarkIndex = hash.indexOf('?');
  if (questionMarkIndex > -1) {
    return parseURLSearch(hash.slice(questionMarkIndex));
  }
  return {};
}
